import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  ContentBlock,
  PatchAdminV1StoresStoreIdBlocksIdBody,
  PostAdminV1StoresStoreIdBlocksBody,
} from '@nucleus-commerce/api-client/admin';

import { blocksApi } from '../api/content-api';

import { contentKeys, isAnyBlockList } from './content-keys';

/**
 * A block change touches every list that could show it (store lists include shared blocks) and
 * every section that places it, so both families are invalidated rather than one store's.
 */
function remember(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string | null,
  row: ContentBlock,
) {
  queryClient.setQueryData(contentKeys.blockDetail(storeId, row.id), row);
  return invalidateAround(queryClient);
}

function invalidateAround(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ predicate: (query) => isAnyBlockList(query.queryKey) }),
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'stores' && query.queryKey[2] === 'sections',
    }),
  ]);
}

/** Field errors (key taken, a missing alt text) are rendered by the form, not toasted. */
export function useCreateBlock(storeId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdBlocksBody) => blocksApi.create(storeId, body),
    meta: { successMessage: 'content:toasts.blockCreated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useUpdateBlock(storeId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdBlocksIdBody }) =>
      blocksApi.update(storeId, id, body),
    meta: { successMessage: 'content:toasts.blockUpdated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useDeleteBlock(storeId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => blocksApi.remove(storeId, id),
    meta: { successMessage: 'content:toasts.blockDeleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: contentKeys.blockDetail(storeId, id) });
      return invalidateAround(queryClient);
    },
  });
}
